import { canStartTemplate, creditedRequirement, PARTY_LABEL, windowDays } from '../../audit/requirements';
import { Modal } from '../../ui/Modal';
import { appliesTo } from '../../audit/assignment';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useAppUser } from '../../auth/AppUserContext';
import { getVisibleStores } from '../../data/stores';
import {
  getPublishedTemplates,
  type PublishedTemplate,
} from '../../data/templates';
import {
  getMyOpenServerAudits,
  type AuditRecord,
  type ServerAudit,
} from '../../data/audits';
import {
  cancelLocalAudit,
  cancelServerAudit,
  dropExpiredServerAudits,
  listMyLocalAudits,
  reconcileWithServer,
  resumeFromServer,
  startAudit,
} from '../../audit/lifecycle';
import { getSyncEngine, sessionStore } from '../../local';
import { coverWithoutStores, getRecentAudits, summariesCover } from '../../data/districtSummaries';
import { isNetworkFailure, OFFLINE_LOCAL_MESSAGE } from '../../api/offline';
import { CancelAuditDialog } from '../../ui/audits/CancelAuditDialog';
import { ActionButton, useActionLock } from '../../ui/ActionButton';
import { ExpiryNotice } from '../../ui/audits/ExpiryNotice';
import { closedAuditMessage, pendingAdditive } from '../../ui/audits/closedAudit';
import { answerProgress, answeredCount } from '../../audit/answers';
import { canConductAudits } from '../../data/scope';
import type { Store } from '../../data/types';
import type { LocalAuditSession } from '../../local/types';
import { CompletedAuditsList } from '../../ui/audits/CompletedAuditsList';
import { StoreSearchSelect } from '../../ui/StoreSearchSelect';
import { formatDateTime } from '../../ui/dates';
import { plural } from '../../ui/plural';

/** Unfiltered default view caps at this; any active filter shows all matches. */
const COMPLETED_SHOWN = 20;

/** A failed server read on the hub: offline still shows this device's audits. */
const listFailure = (err: unknown) =>
  isNetworkFailure(err) ? OFFLINE_LOCAL_MESSAGE : err instanceof Error ? err.message : String(err);

/** Reconnect + a finished sync often arrive together: one reload for both. */
const REFRESH_DEBOUNCE_MS = 1500;

/**
 * Audit hub: resume in-progress audits (this device or synced from another
 * one) and start new ones. Store choices respect role scoping and hide
 * inactive stores; templates come from Published AuditTemplates rows.
 */
export function AuditsPage() {
  const { user, realUser, summaryDistricts, impersonating } = useAppUser();
  const navigate = useNavigate();

  const [localAudits, setLocalAudits] = useState<LocalAuditSession[] | null>(null);
  const [serverAudits, setServerAudits] = useState<ServerAudit[]>([]);
  const [completed, setCompleted] = useState<AuditRecord[] | null>(null);
  const [stores, setStores] = useState<Store[]>([]);
  const [storesLoaded, setStoresLoaded] = useState(false);
  /** Stores or audit types failed to load — the start dialog says so (with Retry). */
  const [startLoadError, setStartLoadError] = useState<string | null>(null);
  /** Bumped per load of the start lists — a late answer for an earlier person ("View as") is dropped. */
  const startLoadGen = useRef(0);
  const [templates, setTemplates] = useState<PublishedTemplate[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** The server lists couldn't be read (shown apart from action errors; cleared by the next good read). */
  const [listError, setListError] = useState<string | null>(null);
  /** Bumped when the signal returns or a sync finishes: re-read the server lists (round 2 F2). */
  const [refreshTick, setRefreshTick] = useState(0);
  /** Start / Resume here: one at a time, and a double tap starts one audit. */
  const lock = useActionLock();
  const busy = lock.busy;

  /** The audit whose Cancel confirm is open (local copy or server-only row). */
  const [cancelling, setCancelling] = useState<
    | { kind: 'local'; session: LocalAuditSession }
    | { kind: 'server'; audit: ServerAudit; label: string }
    | null
  >(null);

  const [storeId, setStoreId] = useState('');
  const [templateId, setTemplateId] = useState('');
  /** The "Start an audit" modal (store + audit type live inside it). */
  const [starting, setStarting] = useState(false);

  // Store teams conduct their own self-audits (owner rulings 2026-10-19).
  const canConduct = user !== null && (canConductAudits(user.role) || user.role === 'StoreTeam');
  // Templates this person may start (the REAL role decides, like the clock),
  // and — once a store is picked — only those assigned to that store.
  const startable = useMemo(
    () => (templates ?? []).filter((t) => user !== null && canStartTemplate((realUser ?? user).role, t.requirements)),
    [templates, user, realUser],
  );
  const startStores = useMemo(
    () => stores.filter((s) => startable.some((t) => appliesTo(t.assignment, s))),
    [stores, startable],
  );
  /**
   * Both lists the start dialog needs are in. Until then it says it's
   * loading: offering the picker early showed an empty list, then swapped
   * the field for "no audit type applies" and back, which left it blank and
   * closed under the user's typing (2026-10-19 DM exploration + sweep L2).
   */
  const startReady = templates !== null && storesLoaded;
  const pickedStore = stores.find((s) => s.id === storeId);
  const templateOptions = pickedStore ? startable.filter((t) => appliesTo(t.assignment, pickedStore)) : startable;

  useEffect(() => {
    if (!user) return;
    setStoresLoaded(false); // "View as" changed who we load for
    setStartLoadError(null);
    const gen = ++startLoadGen.current;
    // The start dialog shows these two failures itself (with Try again).
    const startListFailed = (err: unknown) => {
      if (gen !== startLoadGen.current) return;
      setListError(listFailure(err));
      setStartLoadError(err instanceof Error ? err.message : String(err));
    };
    getVisibleStores(user)
      .then((s) => {
        if (gen !== startLoadGen.current) return;
        setStores(s);
        setStoresLoaded(true);
      })
      .catch(startListFailed);
    loadCompleted();
    if (!canConductAudits(user.role) && user.role !== 'StoreTeam') return;
    getPublishedTemplates()
      .then((t) => {
        if (gen === startLoadGen.current) setTemplates(t);
      })
      .catch(startListFailed);
    loadMine();
  }, [user, realUser]);

  // Only the LATEST read may land (QA 2026-10-22): a read started before a
  // sync can finish after the post-sync refresh and would put the old list back.
  const completedGen = useRef(0);
  const mineGen = useRef(0);

  /**
   * Completed audits: from the monthly district summaries when they cover
   * everything this viewer sees (round 4, 2026-10-24: an Executive's hub read
   * all ~2,700 audits to show 20), else from the audits list as before.
   */
  function loadCompleted() {
    if (!user) return;
    const gen = ++completedGen.current;
    const decided = coverWithoutStores(summaryDistricts, impersonating);
    const show = (r: { audits: AuditRecord[] }) => {
      if (gen !== completedGen.current) return;
      setCompleted(r.audits);
      setListError(null);
    };
    (decided !== null
      ? Promise.resolve(decided)
      : getVisibleStores(user).then(
          (list) => summariesCover(summaryDistricts, list, impersonating),
          // A failed store read falls back to the audits read (QA 2026-10-24).
          () => false,
        )
    )
      .then((cover) => getRecentAudits(cover, { onUpdate: show }))
      .then(show)
      .catch((err: unknown) => {
        if (gen === completedGen.current) setListError(listFailure(err));
      });
  }

  /** This account's audits on this device + its open audits on the server. */
  function loadMine() {
    if (!user) return;
    const gen = ++mineGen.current;
    const latest = () => gen === mineGen.current;
    // Shared devices: only the signed-in account's own saved audits.
    const localLoad = listMyLocalAudits((realUser ?? user).upn);
    void localLoad.then((l) => {
      if (latest()) setLocalAudits(l);
    });
    // Server-side open audits are a best-effort enhancement — offline is fine.
    // Once both are in: repair drifted start dates, expire anything past
    // its 72 hours, and close local copies another device has taken over
    // or closed. Expired server-only rows are marked and dropped.
    // The REAL account's audits — "View as" is display-only and must not
    // offer the impersonated person's audits as resumable on this device.
    getMyOpenServerAudits((realUser ?? user).upn)
      .then(async (server) => {
        const local = await localLoad;
        const reconciled = await reconcileWithServer(local, server);
        const open = await dropExpiredServerAudits(server, local);
        if (!latest()) return;
        setLocalAudits(reconciled);
        setServerAudits(open);
      })
      .catch(() => {
        if (latest()) setServerAudits([]);
      });
  }

  // The device's own list moves at once when a sync finishes (round 4: the
  // just-synced audit still sat under "Waiting to sync" for the debounce).
  const localUpn = useRef('');
  localUpn.current = (realUser ?? user)?.upn ?? '';
  useEffect(() => {
    let prev: string | null = null;
    return getSyncEngine().subscribe((s) => {
      const finished = prev !== null && prev !== 'synced' && s.state === 'synced';
      prev = s.state;
      if (!finished || !localUpn.current) return;
      void listMyLocalAudits(localUpn.current)
        .then(setLocalAudits)
        .catch(() => undefined);
    });
  }, []);

  // Signal back, or a sync just finished (a submitted audit landed): the
  // hub re-reads the server so "Waiting to sync" → Completed without a
  // reload. Only a run that had work to do ends in a change to 'synced'
  // (an idle 30 s check stays 'synced'), so this never polls.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const refresh = () => {
      clearTimeout(timer);
      timer = setTimeout(() => setRefreshTick((n) => n + 1), REFRESH_DEBOUNCE_MS);
    };
    let prev: string | null = null;
    const unsubscribe = getSyncEngine().subscribe((s) => {
      // A finished sync re-reads at once (the device list already moved — QA
      // 2026-10-24: the audit was briefly in neither list); 'online' debounces.
      if (prev !== null && prev !== 'synced' && s.state === 'synced') setRefreshTick((n) => n + 1);
      prev = s.state;
    });
    window.addEventListener('online', refresh);
    return () => {
      clearTimeout(timer);
      unsubscribe();
      window.removeEventListener('online', refresh);
    };
  }, []);

  useEffect(() => {
    if (refreshTick === 0 || !user) return;
    loadCompleted();
    // The start dialog's lists, if they failed while offline.
    if (!storesLoaded || (canConduct && templates === null)) retryStartLoad();
    if (canConductAudits(user.role) || user.role === 'StoreTeam') loadMine();
    // Only on a tick — the first load is the effect above.
  }, [refreshTick]);

  // Opened before the lists arrived: make the lone-store / lone-type picks
  // the button would have made, unless the user already chose something.
  useEffect(() => {
    if (!starting || !startReady) return;
    const lone = startStores.length === 1 ? startStores[0]! : null;
    if (!lone) return;
    const options = startable.filter((t) => appliesTo(t.assignment, lone));
    setStoreId((cur) => (cur === '' ? lone.id : cur));
    setTemplateId((cur) => (cur === '' && options.length === 1 ? String(options[0]!.id) : cur));
    // Only when the dialog opens or the lists finish loading.
  }, [starting, startReady]);

  // Completed audits scoped to visible (active) stores, newest first.
  const visibleCompleted = useMemo(() => {
    if (completed === null) return null;
    const byId = new Map(stores.map((s) => [Number(s.id), s]));
    return completed
      .filter((a) => byId.has(a.storeId))
      .map((a) => ({ audit: a, store: byId.get(a.storeId)! }));
  }, [completed, stores]);

  // Server audits that no local session already tracks (cross-device).
  const resumableFromServer = useMemo(() => {
    if (!localAudits) return [];
    const knownServerIds = new Set(
      localAudits.map((s) => s.serverId).filter((id): id is number => id !== null),
    );
    const knownLocalIds = new Set(localAudits.map((s) => s.localId));
    return serverAudits.filter(
      (a) =>
        !knownServerIds.has(a.id) &&
        (a.originLocalId === null || !knownLocalIds.has(a.originLocalId)),
    );
  }, [localAudits, serverAudits]);

  // A failed start's error belongs to the modal; don't strand it on the page.
  /** The start dialog's Retry: loads whichever of its two lists failed. */
  const retryStartLoad = () => {
    if (!user) return;
    setStartLoadError(null);
    setError(null);
    const gen = startLoadGen.current;
    const fail = (err: unknown) => {
      if (gen === startLoadGen.current) setStartLoadError(err instanceof Error ? err.message : String(err));
    };
    if (!storesLoaded) {
      getVisibleStores(user)
        .then((s) => {
          if (gen !== startLoadGen.current) return;
          setStores(s);
          setStoresLoaded(true);
        })
        .catch(fail);
    }
    if (templates === null) {
      getPublishedTemplates()
        .then((t) => {
          if (gen === startLoadGen.current) setTemplates(t);
        })
        .catch(fail);
    }
  };

  const closeStart = () => {
    setStarting(false);
    setError(null);
  };

  const onStart = async () => {
    const store = stores.find((s) => s.id === storeId);
    const template = templateOptions.find((t) => String(t.id) === templateId);
    if (!store || !template || !user) return;
    try {
      // The REAL role decides which clock it resets ("View as" never changes it).
      const session = await startAudit(store, template, user.upn, (realUser ?? user).role, (realUser ?? user).upn);
      navigate(`/audits/${session.localId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const onResumeFromServer = async (audit: ServerAudit) => {
    if (!user) return;
    const store = stores.find((s) => Number(s.id) === audit.storeId);
    if (!store) {
      setError(
        'This audit belongs to a store outside your current scope (or an inactive store).',
      );
      return;
    }
    try {
      const session = await resumeFromServer(audit, store, user.upn, (realUser ?? user).role, (realUser ?? user).upn);
      navigate(`/audits/${session.localId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const onConfirmCancel = async () => {
    if (!cancelling) return;
    if (cancelling.kind === 'local') {
      const closed = await cancelLocalAudit(cancelling.session.localId);
      setLocalAudits((prev) =>
        (prev ?? []).map((s) => (s.localId === closed.localId ? closed : s)),
      );
    } else {
      await cancelServerAudit(cancelling.audit.id);
      setServerAudits((prev) => prev.filter((a) => a.id !== cancelling.audit.id));
    }
    setCancelling(null);
  };

  const removeLocalCopy = async (localId: string) => {
    setError(null);
    try {
      await sessionStore.deleteSession(localId);
      setLocalAudits((prev) => (prev ?? []).filter((s) => s.localId !== localId));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  if (!user) return null;

  const inProgress = (localAudits ?? []).filter((s) => s.status === 'in_progress');
  // Submitted, cancelled or expired here, with uploads still pending.
  const awaitingSync = (localAudits ?? []).filter(
    (s) =>
      (s.status === 'submitted' || s.status === 'cancelled' || s.status === 'expired') &&
      (s.headerDirty || s.dirtyQuestions.length > 0 || pendingAdditive(s) > 0),
  );
  const closedElsewhere = (localAudits ?? []).filter(
    (s) => s.status === 'closed_elsewhere',
  );

  return (
    <div>
      <div className="page-head">
        <h1>Audits</h1>
        {canConduct && (!startReady || startStores.length > 0) && (
          <button
            className="btn btn-primary"
            onClick={() => {
              // Fresh picks each open; a lone store / template needs no choosing.
              const lone = startStores.length === 1 ? startStores[0]! : null;
              const options = lone ? startable.filter((t) => appliesTo(t.assignment, lone)) : startable;
              setStoreId(lone ? lone.id : '');
              setTemplateId(options.length === 1 ? String(options[0]!.id) : '');
              setError(null);
              setStarting(true);
            }}
            disabled={busy}
          >
            Start an audit
          </button>
        )}
      </div>
      {/* Round 5 (store): "Why can't I start an audit?" — say it, don't just hide the button. */}
      {user.role === 'StoreTeam' && startReady && startStores.length === 0 && (
        <p className="muted">
          Your DM and Auditors start your store’s audits. Each one appears under Completed audits once it’s submitted.
        </p>
      )}
      {listError && !starting && <p className="error">{listError}</p>}
      {error && !starting && <p className="error">{error}</p>}

      {canConduct && (
        <>
          <h2>In progress on this device</h2>
          {localAudits === null && <p>Loading…</p>}
          {localAudits !== null && inProgress.length === 0 && (
            <p className="muted">Nothing in progress.</p>
          )}
          {inProgress.map((s) => {
            const p = answerProgress(s.definition, s.answers);
            return (
              <div className="audit-row" key={s.localId}>
                <div>
                  <strong>
                    {s.storeNumber} — {s.storeName}
                  </strong>
                  <div className="muted">
                    {s.templateName} · started {formatDateTime(s.startedAt)} ·{' '}
                    {p.total > 0 ? `${p.answered} of ${p.total} required answered` : `${answeredCount(s.definition, s.answers)} answered`}
                    {s.dirtyQuestions.length > 0 &&
                      ` · ${s.dirtyQuestions.length} not yet synced`}
                    {' · '}
                    <ExpiryNotice startedAt={s.startedAt} />
                  </div>
                </div>
                <div className="audit-row-actions">
                  <button
                    className="btn btn-danger"
                    onClick={() => setCancelling({ kind: 'local', session: s })}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => navigate(`/audits/${s.localId}`)}
                  >
                    Resume
                  </button>
                </div>
              </div>
            );
          })}

          {resumableFromServer.length > 0 && (
            <>
              <h2>In progress — saved online</h2>
              <p className="muted">
                Not saved on this device (started somewhere else, or cleared
                from this device at sign-out). Resuming brings back everything
                that finished uploading; anything still waiting on another
                device isn’t included, and that device stops editing it.
              </p>
              {resumableFromServer.map((a) => {
                const store = stores.find((s) => Number(s.id) === a.storeId);
                return (
                  <div className="audit-row audit-row-stack" key={a.id}>
                    <div>
                      <strong>
                        {store ? `${store.storeNumber} — ${store.name}` : 'Unknown store'}
                      </strong>
                      <div className="muted">
                        started {formatDateTime(a.startedAt)} ·{' '}
                        <ExpiryNotice startedAt={a.startedAt} />
                      </div>
                    </div>
                    <div className="audit-row-actions">
                      <button
                        className="btn btn-danger"
                        disabled={busy}
                        onClick={() =>
                          setCancelling({
                            kind: 'server',
                            audit: a,
                            label: store
                              ? `${store.storeNumber} — ${store.name}`
                              : 'Unknown store',
                          })
                        }
                      >
                        Cancel
                      </button>
                      <ActionButton
                        className="btn"
                        lock={lock}
                        busyLabel="Resuming…"
                        onAction={() => onResumeFromServer(a)}
                      >
                        Resume here
                      </ActionButton>
                    </div>
                  </div>
                );
              })}
            </>
          )}

          {awaitingSync.length > 0 && (
            <>
              <h2>Waiting to sync</h2>
              {awaitingSync.map((s) => (
                <div className="audit-row" key={s.localId}>
                  <div>
                    <strong>
                      {s.storeNumber} — {s.storeName}
                    </strong>
                    <div className="muted">
                      {s.templateName} ·{' '}
                      {s.status === 'submitted'
                        ? `submitted ${formatDateTime(s.submittedAt)}`
                        : s.status === 'cancelled'
                          ? 'cancelled'
                          : 'expired'}{' '}
                      · uploads automatically when online
                    </div>
                  </div>
                  <button
                    className="btn"
                    onClick={() => navigate(`/audits/${s.localId}`)}
                  >
                    View
                  </button>
                </div>
              ))}
            </>
          )}
        </>
      )}

      {canConduct && closedElsewhere.length > 0 && (
        <>
          <h2>Closed on another device</h2>
          {closedElsewhere.map((s) => {
            const uploading = pendingAdditive(s);
            const orphaned = s.closedElsewhere?.reason === 'removed';
            return (
              <div className="audit-row" key={s.localId}>
                <div>
                  <strong>
                    {s.storeNumber} — {s.storeName}
                  </strong>
                  <div className="muted">
                    {s.templateName} · started {formatDateTime(s.startedAt)} ·{' '}
                    {closedAuditMessage(s)}
                    {uploading > 0 &&
                      !orphaned &&
                      ` Uploading ${uploading} note/task/photo item${uploading > 1 ? 's' : ''} from this device…`}
                  </div>
                </div>
                <ActionButton
                  className="btn"
                  busyLabel="Removing…"
                  disabled={uploading > 0 && !orphaned}
                  title={
                    uploading > 0 && !orphaned
                      ? 'Available once this device’s notes, tasks and photos have uploaded'
                      : undefined
                  }
                  onAction={() => removeLocalCopy(s.localId)}
                >
                  Remove from this device
                </ActionButton>
              </div>
            );
          })}
        </>
      )}

      <h2>Completed audits</h2>
      <p className="muted" style={{ fontSize: '0.85rem', margin: '0 0 0.5rem' }}>
        The last 13 months — older audits are on {stores.length === 1 ? 'your store’s page' : 'each store’s page'}.
      </p>
      <CompletedAuditsList
        items={visibleCompleted}
        filterStores={stores}
        cap={COMPLETED_SHOWN}
      />

      {cancelling && (
        <CancelAuditDialog
          storeLabel={
            cancelling.kind === 'local'
              ? `${cancelling.session.storeNumber} — ${cancelling.session.storeName}`
              : cancelling.label
          }
          onConfirm={onConfirmCancel}
          onClose={() => setCancelling(null)}
        />
      )}

      {starting && (
        <Modal onClose={closeStart} busy={busy}>
            <h2>Start an audit</h2>
            {!startReady && startLoadError ? (
              <div role="alert">
                <p className="error">Couldn’t load your stores and audit types — {startLoadError}</p>
                <button className="btn" onClick={retryStartLoad}>
                  Try again
                </button>
              </div>
            ) : !startReady ? (
              <p className="muted" role="status">
                Loading your stores and audit types…
              </p>
            ) : startStores.length === 0 ? (
              <p className="muted">
                No published audit type you can start applies to your stores. Admin → Templates
                publishes them and assigns them to stores.
              </p>
            ) : (
              <div className="form-grid">
                <label className="span-2">
                  Store
                  <StoreSearchSelect
                    stores={startStores}
                    value={storeId}
                    onChange={(id) => {
                      setStoreId(id);
                      // Keep the audit type only if it's assigned to the new store.
                      const s = stores.find((x) => x.id === id);
                      const options = s ? startable.filter((t) => appliesTo(t.assignment, s)) : startable;
                      // Never silently swap a chosen type for another: a type that
                      // doesn't apply here is cleared; a lone option fills an empty pick.
                      setTemplateId((cur) =>
                        cur !== ''
                          ? options.some((t) => String(t.id) === cur)
                            ? cur
                            : ''
                          : options.length === 1
                            ? String(options[0]!.id)
                            : '',
                      );
                    }}
                    required
                    placeholder="Type to search stores…"
                  />
                </label>
                <label className="span-2">
                  Audit type
                  <select
                    value={templateId}
                    onChange={(e) => setTemplateId(e.target.value)}
                    required
                  >
                    <option value="">— select audit type —</option>
                    {templateOptions.map((t) => (
                      <option key={t.id} value={t.id}>
                        {/* The version means something only to admins (final run: "(v1)" confused DMs). */}
                        {user?.role === 'Admin' ? `${t.name} (v${t.version})` : t.name}
                      </option>
                    ))}
                  </select>
                  {(() => {
                    const t = templateOptions.find((x) => String(x.id) === templateId);
                    if (!t || !user) return null;
                    const credited = creditedRequirement(t.requirements, (realUser ?? user).role);
                    return (
                      <span className="muted">
                        {credited
                          ? `Counts toward the ${PARTY_LABEL[credited.party]} audit (every ${plural(windowDays(credited, pickedStore?.longDistance === true), 'day')}${pickedStore?.longDistance && credited.longDistanceDays ? ', long-distance store' : ''}).`
                          : 'This audit type has no requirement for your role — it is recorded and scored but resets no due date.'}
                      </span>
                    );
                  })()}
                </label>
              </div>
            )}
            {error && error !== startLoadError && <p className="error">{error}</p>}
            <div className="modal-actions">
              <button className="btn" disabled={busy} onClick={closeStart}>
                Cancel
              </button>
              <ActionButton
                className="btn btn-primary"
                lock={lock}
                busyLabel="Starting…"
                disabled={!storeId || !templateId}
                onAction={onStart}
              >
                Start audit
              </ActionButton>
            </div>
        </Modal>
      )}
    </div>
  );
}

